import { store } from "@/store/index";
import { ENDPOINTS, SESAME_GATE_SERVICE } from "@/app/app-config";
import { ROUTES } from "@/app/app-constants";
import { getSecurityToken } from "@/federation/getSecurityToken";
import { pendoService } from "@/services/pendo/Pendo";
import rumService, { RUM_EVENT_TYPES, type RumEventType } from "@/utils/awsRUM/awsRUM";

interface AuthRealmDomain {
  realm: string;
  domain: string;
}

// Resolved at CALL time — realm/domain land in authInfo during app init,
// after this module loads. A mount-time snapshot would race initialization.
const getSesameEndpoint = (): string => {
  const { authInfo } = store.getState() as { authInfo?: Partial<AuthRealmDomain> };
  const realm = authInfo?.realm;
  const domain = authInfo?.domain;
  if (!realm || !domain) {
    throw new Error("ownerConfigHostServices: authInfo realm/domain not initialized");
  }
  return ENDPOINTS[realm][domain][SESAME_GATE_SERVICE];
};

/**
 * The host's conformance to the OwnerConfig package's HostServices interface
 * (consumer-defines-the-interface DI — see owner-config-extraction design
 * §0b). Passed to <OwnerConfigApp hostServices={...}> at the lazy route.
 * Kept as a module singleton so the prop is referentially stable across
 * host re-renders.
 */
export const ownerConfigHostServices = {
  getSecurityToken,
  getSesameEndpoint,
  routes: {
    ownerConfiguration: ROUTES.OWNER_CONFIGURATION,
    ownerConfigurationCreate: ROUTES.OWNER_CONFIGURATION_CREATE,
  },
  telemetry: {
    trackPendoEvent: (eventName: string, data?: Record<string, unknown>) =>
      pendoService.trackEvent(eventName, data),
    trackRumCustomEvent: (event: {
      type: string;
      message: string;
      data?: Record<string, unknown>;
    }) =>
      rumService.customEvent({
        // The child speaks plain strings; narrow to the host's RUM enum,
        // falling back to USER_ACTION for unknown types.
        type: (Object.values(RUM_EVENT_TYPES) as string[]).includes(event.type)
          ? (event.type as RumEventType)
          : RUM_EVENT_TYPES.USER_ACTION,
        message: event.message,
        data: event.data,
      }),
  },
};
